"use client";

import React from "react";
import { ArrowUpRight, Boxes, ClipboardCheck, PhoneCall } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function ComparePrices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="comparepricescta" data-nav="light" className="relative w-full bg-background py-16 sm:py-24 px-4 sm:px-6 lg:px-8 border-t border-border overflow-hidden">
      {/* Precision Datum Grid Background (subtle technical accents) */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[radial-gradient(#000000_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="relative mx-auto max-w-5xl">
        {/* Machined Plate Container */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="relative bg-card text-card-foreground border border-border shadow-sm p-6 sm:p-10 lg:p-14">
          {/* Industrial Corner Calibration Marks */}
          <div aria-hidden="true" className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-foreground/70 -translate-x-[1px] -translate-y-[1px]" />
          <div aria-hidden="true" className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-foreground/70 translate-x-[1px] -translate-y-[1px]" />
          <div aria-hidden="true" className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-foreground/70 -translate-x-[1px] translate-y-[1px]" />
          <div aria-hidden="true" className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-foreground/70 translate-x-[1px] translate-y-[1px]" />

          {/* Technical Spec Header Strip */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/80 pb-4 mb-8 text-xs font-mono tracking-wider text-muted-foreground uppercase">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-primary" />
              <span>Commercial Equipment Procurement</span>
            </div>
            <div className="flex items-center text-[11px]">
              <span>U.S. FREIGHT &amp; PALLETIZED DISPATCH</span>
            </div>
          </div>

          <div className="max-w-3xl">
            {/* Heading */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground leading-[1.08] mb-6">
              Compare Current Mechanical Bull Prices
            </h2>

            {/* Narrative Body Copy */}
            <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed mb-8">
              The most useful mechanical bull price is the price of a machine that actually fits your needs. Compare current new and used inventory, check exactly what comes with each package and review the product information before making an enquiry. If you have narrowed your choice to a specific machine, contact us with the product name or SKU so we can confirm the information currently available for that listing.
            </p>
          </div>

          {/* Specification Highlights Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2 pb-8 mb-8 border-y border-border/60 text-xs font-mono text-muted-foreground">
            <div className="flex items-center gap-2">
              <Boxes className="w-4 h-4 text-primary shrink-0" />
              <span>Compare current new &amp; used inventory</span>
            </div>
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-primary shrink-0" />
              <span>Review exact package inclusions per listing</span>
            </div>
            <div className="flex items-center gap-2">
              <PhoneCall className="w-4 h-4 text-primary shrink-0" />
              <span>Direct SKU confirmation via equipment team</span>
            </div>
          </div>

          {/* Action Buttons Group (Angular, 0px radius industrial aesthetic) */}
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3.5">
            {/* Primary Button */}
            <motion.div whileTap={shouldReduceMotion ? undefined : {
            scale: 0.99
          }}>
              <Button asChild className="w-full sm:w-auto h-12 px-6 rounded-none bg-primary text-primary-foreground font-mono text-xs uppercase tracking-wider font-semibold hover:bg-primary/90 shadow-none transition-colors border border-primary">
                <Link to="/NewMechanicalBullsPage" className="inline-flex items-center justify-center gap-2">
                  <span>VIEW NEW BULLS</span>
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </Button>
            </motion.div>

            {/* Secondary Button */}
            <motion.div whileTap={shouldReduceMotion ? undefined : {
            scale: 0.99
          }}>
              <Button asChild variant="outline" className="w-full sm:w-auto h-12 px-6 rounded-none border border-secondary text-secondary bg-transparent font-mono text-xs uppercase tracking-wider font-semibold hover:bg-secondary hover:text-secondary-foreground shadow-none transition-colors">
                <Link to="/UsedMechanicalBullsPage" className="inline-flex items-center justify-center gap-2">
                  <span>VIEW USED BULLS</span>
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </Button>
            </motion.div>

            {/* Tertiary / Contact Button */}
            <motion.div whileTap={shouldReduceMotion ? undefined : {
            scale: 0.99
          }}>
              <Button asChild className="w-full sm:w-auto h-12 px-6 rounded-none bg-secondary text-secondary-foreground font-mono text-xs uppercase tracking-wider font-semibold hover:bg-secondary/90 shadow-none transition-colors border border-secondary">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>CONTACT US</span>
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Bottom Datum Specification Reference */}
          <div className="mt-8 pt-4 border-t border-border/40 flex flex-wrap items-center justify-between text-[11px] font-mono text-muted-foreground/80">
            <span>DIRECT EQUIPMENT SALES - NO RENTALS</span>
            <span>U.S. FREIGHT QUOTES GENERATED PER LOCATION</span>
          </div>
        </motion.div>
      </div>
    </section>;
}